import { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";

const PAGE_SIZE = 40;

const FACET_DEFS = [
  { key: "publicationTypes", label: "Publication type" },
  { key: "campuses", label: "Campus" },
  { key: "schools", label: "School" },
  { key: "departments", label: "Department" },
  { key: "centers", label: "Research center" },
  { key: "thematicAreas", label: "Thematic area" },
  { key: "researchAreas", label: "Research area" },
  { key: "verification", label: "Verification" },
  { key: "amritaAffiliations", label: "Amrita affiliation" },
  { key: "initiatives", label: "Initiative" },
  { key: "tagGroups", label: "Research tag group" },
  { key: "tags", label: "Tag" },
  { key: "publisher", label: "Publisher" },
  { key: "source", label: "Source / journal" },
];

function makeEmptyFilters() {
  return {
    yearFrom: "",
    yearTo: "",
    hasAbstract: false,
    hasExternalUrl: false,
    ...Object.fromEntries(FACET_DEFS.map(({ key }) => [key, []])),
  };
}

function catalogAssetUrl(path) {
  const publicUrl = String(process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${publicUrl}/${path}`;
}

function normalizeCatalogPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.records)) return payload.records;
  return [];
}

async function loadCatalog(signal) {
  const manifestResponse = await fetch(`${catalogAssetUrl("catalog/manifest.json")}?v=${Date.now()}`, { signal, cache: "no-store" });
  if (!manifestResponse.ok) throw new Error(`Catalog manifest request failed (${manifestResponse.status})`);
  const manifest = await manifestResponse.json();
  const chunks = Array.isArray(manifest.chunks) ? manifest.chunks.filter(Boolean) : [];
  if (chunks.length === 0) throw new Error("The catalog manifest does not contain any data chunks.");

  const version = manifest.version ? `?v=${encodeURIComponent(manifest.version)}` : "";
  const payloads = await Promise.all(chunks.map(async (chunk) => {
    const response = await fetch(`${catalogAssetUrl(`catalog/${chunk}`)}${version}`, { signal, cache: "no-store" });
    if (!response.ok) throw new Error(`Catalog data request failed (${response.status})`);
    return response.json();
  }));
  return payloads.flatMap(normalizeCatalogPayload);
}

function asArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === null || value === undefined || value === "") return [];
  return [value];
}

function tokenize(input) {
  return String(input || "")
    .trim()
    .toLocaleLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function countOccurrences(haystack, needle) {
  if (!needle) return 0;
  let count = 0;
  let cursor = 0;
  while ((cursor = haystack.indexOf(needle, cursor)) !== -1) {
    count += 1;
    cursor += needle.length;
  }
  return count;
}

function recordSearchText(record) {
  const parts = [
    record.title,
    record.publisher,
    record.source,
    record.abstract,
    record.citation,
    ...FACET_DEFS.flatMap(({ key }) => asArray(record[key])),
  ];
  return parts.filter(Boolean).join(" ").toLocaleLowerCase();
}

function formatList(values, limit = 3) {
  const list = asArray(values);
  if (list.length <= limit) return list.join(", ");
  return `${list.slice(0, limit).join(", ")} +${list.length - limit}`;
}

function displayDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const detailCache = new Map();

function detailValue(element) {
  const strong = element.querySelector("strong");
  if (!strong) return element.textContent.trim();
  const label = strong.textContent.trim();
  return element.textContent.trim().slice(label.length).replace(/^\s*:\s*/, "").trim();
}

function parsePublicationHtml(markup) {
  const document = new DOMParser().parseFromString(markup, "text/html");
  const box = document.querySelector(".publictn-data");
  if (!box) return {};
  const fields = {};
  const labelMap = {
    "publication type": "publicationType",
    publisher: "publisher",
    source: "source",
    url: "externalUrl",
    campus: "campus",
    school: "school",
    department: "department",
    center: "center",
    year: "year",
    keywords: "keywords",
    abstract: "abstract",
    "cite this research publication": "citation",
  };

  Array.from(box.children)
    .filter((child) => child.tagName === "P")
    .forEach((paragraph) => {
      const strong = paragraph.querySelector("strong");
      if (!strong) return;
      const label = strong.textContent.replace(/\s+/g, " ").replace(/\s*:\s*$/, "").trim().toLocaleLowerCase();
      const field = labelMap[label];
      if (!field) return;
      const value = detailValue(paragraph);
      if (!value) return;
      if (field === "externalUrl") fields[field] = paragraph.querySelector("a")?.href || value;
      else if (field === "keywords") fields[field] = value.split(/[;,]/).map((item) => item.trim()).filter(Boolean);
      else if (field === "year") fields[field] = Number.parseInt(value, 10) || null;
      else fields[field] = value;
    });

  const abstract = box.querySelector(".abst p");
  if (abstract) fields.abstract = detailValue(abstract);
  return fields;
}

function mergeLiveDetail(record, liveDetail) {
  if (!record) return null;
  const merged = { ...record, ...(liveDetail || {}) };
  const listFields = {
    publicationType: "publicationTypes",
    campus: "campuses",
    school: "schools",
    department: "departments",
    center: "centers",
  };
  Object.entries(listFields).forEach(([field, listField]) => {
    if (liveDetail?.[field] && asArray(record[listField]).length === 0) merged[listField] = [liveDetail[field]];
  });
  return merged;
}

function HighlightedText({ text, tokens }) {
  const source = String(text || "");
  if (!source || tokens.length === 0) return source;

  const uniqueTokens = Array.from(new Set(tokens)).sort((a, b) => b.length - a.length);
  const expression = new RegExp(uniqueTokens.map(escapeRegExp).join("|"), "gi");
  const parts = [];
  let cursor = 0;
  let match;

  while ((match = expression.exec(source)) !== null) {
    if (match.index > cursor) parts.push(source.slice(cursor, match.index));
    parts.push(<mark key={`${match.index}-${match[0]}`}>{match[0]}</mark>);
    cursor = match.index + match[0].length;
    if (match.index === expression.lastIndex) expression.lastIndex += 1;
  }
  if (cursor < source.length) parts.push(source.slice(cursor));
  return parts;
}

function FacetMenu({ definition, options, selected, onChange }) {
  const [facetQuery, setFacetQuery] = useState("");
  const filteredOptions = options.filter(({ value }) => value.toLocaleLowerCase().includes(facetQuery.toLocaleLowerCase()));

  function toggle(value) {
    onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);
  }

  return (
    <details className="facet-menu">
      <summary>
        <span>{definition.label}</span>
        <span className="facet-summary-value">{selected.length || "All"}</span>
      </summary>
      <div className="facet-popover">
        {options.length > 8 && (
          <input
            className="facet-search"
            value={facetQuery}
            onChange={(event) => setFacetQuery(event.target.value)}
            placeholder={`Find ${definition.label.toLocaleLowerCase()}`}
            aria-label={`Find ${definition.label.toLocaleLowerCase()}`}
          />
        )}
        <div className="facet-options">
          {filteredOptions.length === 0 && <span className="empty-facet">No matching values</span>}
          {filteredOptions.map(({ value, count }) => (
            <label className="facet-option" key={value}>
              <input type="checkbox" checked={selected.includes(value)} onChange={() => toggle(value)} />
              <span className="facet-option-label">{value}</span>
              <span className="facet-count">{count.toLocaleString()}</span>
            </label>
          ))}
        </div>
      </div>
    </details>
  );
}

function RecordCard({ record, queryTokens, saved, onSave, onDetails }) {
  const verified = asArray(record.verification).some((value) => value.toLocaleLowerCase() === "yes");
  const summary = record.abstract ? `${record.abstract.slice(0, 220)}${record.abstract.length > 220 ? "..." : ""}` : "";

  return (
    <article className="record-card">
      <div className="record-card-main">
        <div className="record-kicker">
          {record.year && <span>{record.year}</span>}
          {asArray(record.publicationTypes).slice(0, 2).map((type) => <span key={type}>{type}</span>)}
          {verified && <span className="verified-pill">Verified</span>}
        </div>
        <h2 className="record-title"><HighlightedText text={record.title} tokens={queryTokens} /></h2>
        <div className="record-meta">
          {record.campuses?.length > 0 && <span>{formatList(record.campuses)}</span>}
          {record.schools?.length > 0 && <span>{formatList(record.schools, 2)}</span>}
          {record.source && <span>{record.source}</span>}
          {record.publisher && <span>{record.publisher}</span>}
        </div>
        {summary && <p className="record-summary"><HighlightedText text={summary} tokens={queryTokens} /></p>}
      </div>
      <div className="record-actions">
        <button className={`save-button${saved ? " is-saved" : ""}`} type="button" onClick={() => onSave(record)}>
          {saved ? "Saved" : "Save"}
        </button>
        <button className="details-button" type="button" onClick={() => onDetails(record)}>Details</button>
        <a className="open-button" href={record.url} target="_blank" rel="noopener noreferrer">Open record</a>
      </div>
    </article>
  );
}

function DetailDialog({ record, saved, onSave, onClose }) {
  const [liveDetail, setLiveDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!record?.url) {
      setLiveDetail(null);
      setLoading(false);
      setError("");
      return undefined;
    }
    const cached = detailCache.get(record.url);
    if (cached) {
      setLiveDetail(cached);
      setLoading(false);
      setError("");
      return undefined;
    }

    const controller = new AbortController();
    setLiveDetail(null);
    setLoading(true);
    setError("");
    fetch(record.url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Request failed (${response.status})`);
        return response.text();
      })
      .then((markup) => {
        const parsed = parsePublicationHtml(markup);
        detailCache.set(record.url, parsed);
        setLiveDetail(parsed);
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setError("Live detail could not be loaded. The catalog record is still available.");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [record?.url]);

  const displayedRecord = mergeLiveDetail(record, liveDetail);
  if (!record || !displayedRecord) return null;
  const metadata = [
    ["Year", displayedRecord.year],
    ["Publication type", formatList(displayedRecord.publicationTypes)],
    ["Campus", formatList(displayedRecord.campuses)],
    ["School", formatList(displayedRecord.schools)],
    ["Department", formatList(displayedRecord.departments)],
    ["Research center", formatList(displayedRecord.centers)],
    ["Thematic area", formatList(displayedRecord.thematicAreas)],
    ["Research area", formatList(displayedRecord.researchAreas)],
    ["Verification", formatList(displayedRecord.verification)],
    ["Amrita affiliation", formatList(displayedRecord.amritaAffiliations)],
    ["Initiative", formatList(displayedRecord.initiatives)],
    ["Tag group", formatList(displayedRecord.tagGroups)],
    ["Publisher", displayedRecord.publisher],
    ["Source / journal", displayedRecord.source],
  ].filter(([, value]) => value);

  async function copyCitation() {
    const citation = displayedRecord.citation || `${displayedRecord.title}${displayedRecord.year ? ` (${displayedRecord.year})` : ""}. ${displayedRecord.url}`;
    try {
      await navigator.clipboard.writeText(citation);
    } catch {
      // Clipboard access can be unavailable on non-secure local origins.
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="detail-dialog" role="dialog" aria-modal="true" aria-labelledby="detail-title">
        <div className="dialog-header">
          <div>
            <div className="eyebrow">PUBLICATION DETAIL</div>
            <h2 id="detail-title">{displayedRecord.title}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close details" title="Close details">X</button>
        </div>
        <div className="dialog-actions">
          <button className={`save-button${saved ? " is-saved" : ""}`} type="button" onClick={() => onSave(displayedRecord)}>
            {saved ? "Saved" : "Save record"}
          </button>
          <button className="secondary-button" type="button" onClick={copyCitation}>Copy citation</button>
          <a className="primary-button" href={displayedRecord.url} target="_blank" rel="noopener noreferrer">Open Amrita record</a>
          {displayedRecord.externalUrl && <a className="secondary-button" href={displayedRecord.externalUrl} target="_blank" rel="noopener noreferrer">Open source</a>}
        </div>
        {loading && <p className="live-status">Loading live publication metadata...</p>}
        {error && <p className="live-status error-status">{error}</p>}
        {metadata.length > 0 && (
          <dl className="detail-grid">
            {metadata.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
          </dl>
        )}
        {displayedRecord.abstract && <div className="detail-section"><h3>Abstract</h3><p>{displayedRecord.abstract}</p></div>}
        {asArray(displayedRecord.keywords).length > 0 && <div className="detail-section"><h3>Keywords</h3><div className="keyword-list">{asArray(displayedRecord.keywords).map((keyword) => <span key={keyword}>{keyword}</span>)}</div></div>}
        {displayedRecord.citation && <div className="detail-section"><h3>Citation</h3><p>{displayedRecord.citation}</p></div>}
        {displayedRecord.updated && <p className="updated-line">Catalog record updated {displayDate(displayedRecord.updated)}</p>}
      </section>
    </div>
  );
}

function exportCsv(records) {
  const escapeCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const header = ["Title", "Year", "Publication type", "Campus", "School", "Department", "Center", "Publisher", "Source", "URL"];
  const rows = records.map((record) => [
    record.title,
    record.year,
    formatList(record.publicationTypes, 99),
    formatList(record.campuses, 99),
    formatList(record.schools, 99),
    formatList(record.departments, 99),
    formatList(record.centers, 99),
    record.publisher,
    record.source,
    record.url,
  ]);
  const csv = [header, ...rows].map((row) => row.map(escapeCell).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "amrita-publications.csv";
  link.click();
  URL.revokeObjectURL(url);
}

export default function App() {
  const [data, setData] = useState([]);
  const [catalogState, setCatalogState] = useState("loading");
  const [catalogError, setCatalogError] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(makeEmptyFilters);
  const [sortMode, setSortMode] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [savedIds, setSavedIds] = useState(() => {
    try {
      return new Set(JSON.parse(window.localStorage.getItem("amrita-publications-saved") || "[]").map(String));
    } catch {
      return new Set();
    }
  });
  const inputRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    setCatalogState("loading");
    setCatalogError("");
    loadCatalog(controller.signal)
      .then((records) => {
        if (records.length === 0) throw new Error("The catalog returned no publication records.");
        setData(records);
        setCatalogState("ready");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        setCatalogError(error.message || "The publication catalog could not be loaded.");
        setCatalogState("error");
      });
    return () => controller.abort();
  }, [loadAttempt]);

  const searchIndex = useMemo(() => data.map(recordSearchText), [data]);
  const titleIndex = useMemo(() => data.map((record) => String(record.title || "").toLocaleLowerCase()), [data]);

  const facetOptions = useMemo(() => {
    const options = {};
    FACET_DEFS.forEach(({ key }) => {
      const counts = new Map();
      data.forEach((record) => asArray(record[key]).forEach((value) => counts.set(value, (counts.get(value) || 0) + 1)));
      options[key] = Array.from(counts, ([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
    });
    return options;
  }, [data]);

  const detailCoverage = useMemo(() => ({
    hasAbstract: data.some((record) => Boolean(record.abstract)),
    hasExternalUrl: data.some((record) => Boolean(record.externalUrl)),
  }), [data]);

  const yearBounds = useMemo(() => {
    const years = data.map((record) => record.year).filter((year) => Number.isInteger(year));
    if (years.length === 0) return { min: "", max: "" };
    return { min: Math.min(...years), max: Math.max(...years) };
  }, [data]);

  const queryTokens = useMemo(() => tokenize(query), [query]);
  const filteredResults = useMemo(() => {
    const results = [];
    const from = filters.yearFrom ? Number(filters.yearFrom) : null;
    const to = filters.yearTo ? Number(filters.yearTo) : null;

    data.forEach((record, index) => {
      if (showSavedOnly && !savedIds.has(String(record.id))) return;
      if (from !== null && (!record.year || record.year < from)) return;
      if (to !== null && (!record.year || record.year > to)) return;
      if (filters.hasAbstract && !record.abstract) return;
      if (filters.hasExternalUrl && !record.externalUrl) return;

      for (const { key } of FACET_DEFS) {
        const selected = filters[key];
        if (selected.length > 0 && !selected.some((value) => asArray(record[key]).includes(value))) return;
      }

      let score = 0;
      if (queryTokens.length > 0) {
        const haystack = searchIndex[index];
        const title = titleIndex[index];
        for (const token of queryTokens) {
          if (!haystack.includes(token)) return;
          score += countOccurrences(title, token) * 100 + (title.includes(token) ? 40 : 0);
        }
        if (title.startsWith(queryTokens.join(" "))) score += 1000;
      }
      results.push({ index, score });
    });

    results.sort((a, b) => {
      const left = data[a.index];
      const right = data[b.index];
      if (sortMode === "relevance" && b.score !== a.score) return b.score - a.score;
      if (sortMode === "oldest" && (left.year || 0) !== (right.year || 0)) return (left.year || 0) - (right.year || 0);
      if (sortMode === "titleAsc") return String(left.title).localeCompare(String(right.title));
      if (sortMode === "titleDesc") return String(right.title).localeCompare(String(left.title));
      if ((right.year || 0) !== (left.year || 0)) return (right.year || 0) - (left.year || 0);
      if (sortMode === "relevance" && a.score !== b.score) return b.score - a.score;
      return String(left.title).localeCompare(String(right.title));
    });
    return results;
  }, [data, filters, queryTokens, searchIndex, showSavedOnly, sortMode, titleIndex, savedIds]);

  const totalPages = Math.max(1, Math.ceil(filteredResults.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageResults = filteredResults.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const activeFilterCount = Object.entries(filters).reduce((count, [key, value]) => {
    if (Array.isArray(value)) return count + value.length;
    return count + (value ? 1 : 0);
  }, 0);

  useEffect(() => {
    window.localStorage.setItem("amrita-publications-saved", JSON.stringify(Array.from(savedIds)));
  }, [savedIds]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, query, showSavedOnly, sortMode]);

  useEffect(() => {
    document.body.classList.toggle("modal-open", Boolean(selectedRecord));
    return () => document.body.classList.remove("modal-open");
  }, [selectedRecord]);

  function updateFacet(key, values) {
    setFilters((current) => ({ ...current, [key]: values }));
  }

  function toggleSaved(record) {
    setSavedIds((current) => {
      const next = new Set(current);
      const id = String(record.id);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function removeFacetValue(key, value) {
    setFilters((current) => ({ ...current, [key]: current[key].filter((item) => item !== value) }));
  }

  function clearAll() {
    setFilters(makeEmptyFilters());
    setQuery("");
    setShowSavedOnly(false);
  }

  if (catalogState === "loading") {
    return (
      <div className="app-shell catalog-state-shell">
        <main className="catalog-state" role="status" aria-live="polite">
          <div className="eyebrow">RESEARCH CATALOG</div>
          <h1>Amrita Publications</h1>
          <p>Loading the publication index...</p>
        </main>
      </div>
    );
  }

  if (catalogState === "error") {
    return (
      <div className="app-shell catalog-state-shell">
        <main className="catalog-state" role="alert">
          <div className="eyebrow">RESEARCH CATALOG</div>
          <h1>Amrita Publications</h1>
          <p>{catalogError}</p>
          <button className="primary-button" type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>Retry loading catalog</button>
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="header-inner">
          <div className="brand-block">
            <div className="eyebrow">RESEARCH CATALOG</div>
            <h1>Amrita Publications</h1>
            <p>Search and explore the university's public research index.</p>
          </div>
          <div className="header-stats" aria-label="Catalog statistics">
            <div><strong>{data.length.toLocaleString()}</strong><span>records</span></div>
            <div><strong>{yearBounds.min}-{yearBounds.max}</strong><span>year range</span></div>
            <div><strong>{facetOptions.campuses?.length || 0}</strong><span>campuses</span></div>
          </div>
        </div>
      </header>

      <main className="content-grid">
        <aside className="filters-panel">
          <div className="panel-heading">
            <div><div className="eyebrow">REFINE INDEX</div><h2>Filters</h2></div>
            <button className="text-button" type="button" onClick={clearAll} disabled={activeFilterCount === 0 && !query && !showSavedOnly}>Clear all</button>
          </div>

          <div className="range-block">
            <div className="filter-label">Publication year</div>
            <div className="range-inputs">
              <input type="number" min={yearBounds.min} max={yearBounds.max} value={filters.yearFrom} onChange={(event) => setFilters((current) => ({ ...current, yearFrom: event.target.value }))} placeholder={String(yearBounds.min)} aria-label="Year from" />
              <span>to</span>
              <input type="number" min={yearBounds.min} max={yearBounds.max} value={filters.yearTo} onChange={(event) => setFilters((current) => ({ ...current, yearTo: event.target.value }))} placeholder={String(yearBounds.max)} aria-label="Year to" />
            </div>
          </div>

          {(detailCoverage.hasAbstract || detailCoverage.hasExternalUrl) && (
            <div className="boolean-filters">
              {detailCoverage.hasAbstract && <label><input type="checkbox" checked={filters.hasAbstract} onChange={(event) => setFilters((current) => ({ ...current, hasAbstract: event.target.checked }))} /> Has abstract</label>}
              {detailCoverage.hasExternalUrl && <label><input type="checkbox" checked={filters.hasExternalUrl} onChange={(event) => setFilters((current) => ({ ...current, hasExternalUrl: event.target.checked }))} /> Has source link</label>}
            </div>
          )}

          <div className="facet-list">
            {FACET_DEFS.filter(({ key }) => facetOptions[key]?.length > 0).map((definition) => (
              <FacetMenu key={definition.key} definition={definition} options={facetOptions[definition.key]} selected={filters[definition.key]} onChange={(values) => updateFacet(definition.key, values)} />
            ))}
          </div>
        </aside>

        <section className="results-panel">
          <div className="search-row">
            <label className="search-box" htmlFor="publication-search">
              <span className="search-glyph" aria-hidden="true">/</span>
              <input id="publication-search" ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search titles, abstracts, journals, campuses, keywords..." autoComplete="off" />
              {query && <button type="button" className="clear-search" onClick={() => { setQuery(""); inputRef.current?.focus(); }} aria-label="Clear search" title="Clear search">X</button>}
            </label>
          </div>

          <div className="result-toolbar">
            <div>
              <strong>{filteredResults.length.toLocaleString()}</strong> matching records
              {query && <span className="toolbar-muted"> for "{query}"</span>}
              {activeFilterCount > 0 && <span className="toolbar-muted"> with {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}</span>}
            </div>
            <div className="toolbar-actions">
              <label className="sort-control">Sort
                <select value={sortMode} onChange={(event) => setSortMode(event.target.value)}>
                  <option value="newest">Newest year</option>
                  <option value="relevance">Relevance</option>
                  <option value="oldest">Oldest year</option>
                  <option value="titleAsc">Title A-Z</option>
                  <option value="titleDesc">Title Z-A</option>
                </select>
              </label>
              <button className={`saved-toggle${showSavedOnly ? " is-active" : ""}`} type="button" onClick={() => setShowSavedOnly((value) => !value)}>Saved {savedIds.size ? `(${savedIds.size})` : ""}</button>
              <button className="secondary-button" type="button" onClick={() => exportCsv(filteredResults.map(({ index }) => data[index]))} disabled={filteredResults.length === 0}>Export CSV</button>
            </div>
          </div>

          {(activeFilterCount > 0 || query || showSavedOnly) && (
            <div className="active-filters" aria-label="Active filters">
              {query && <button type="button" onClick={() => setQuery("")}>Search: {query} X</button>}
              {showSavedOnly && <button type="button" onClick={() => setShowSavedOnly(false)}>Saved only X</button>}
              {filters.yearFrom && <button type="button" onClick={() => setFilters((current) => ({ ...current, yearFrom: "" }))}>From {filters.yearFrom} X</button>}
              {filters.yearTo && <button type="button" onClick={() => setFilters((current) => ({ ...current, yearTo: "" }))}>Through {filters.yearTo} X</button>}
              {FACET_DEFS.flatMap(({ key, label }) => filters[key].map((value) => <button type="button" key={`${key}-${value}`} onClick={() => removeFacetValue(key, value)}>{label}: {value} X</button>))}
              {filters.hasAbstract && <button type="button" onClick={() => setFilters((current) => ({ ...current, hasAbstract: false }))}>Has abstract X</button>}
              {filters.hasExternalUrl && <button type="button" onClick={() => setFilters((current) => ({ ...current, hasExternalUrl: false }))}>Has source link X</button>}
            </div>
          )}

          {pageResults.length > 0 ? (
            <div className="record-list" aria-live="polite">
              {pageResults.map(({ index }) => {
                const record = data[index];
                return <RecordCard key={record.id || record.url} record={record} queryTokens={queryTokens} saved={savedIds.has(String(record.id))} onSave={toggleSaved} onDetails={setSelectedRecord} />;
              })}
            </div>
          ) : (
            <div className="empty-results"><h2>No records match</h2><p>Clear a filter or broaden the search to see more publications.</p><button className="primary-button" type="button" onClick={clearAll}>Reset search</button></div>
          )}

          {filteredResults.length > PAGE_SIZE && (
            <nav className="pagination" aria-label="Publication pages">
              <button className="secondary-button" type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={safePage === 1}>Previous</button>
              <span>Page <strong>{safePage}</strong> of <strong>{totalPages}</strong> <em>({((safePage - 1) * PAGE_SIZE + 1).toLocaleString()}-{Math.min(safePage * PAGE_SIZE, filteredResults.length).toLocaleString()})</em></span>
              <button className="secondary-button" type="button" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={safePage === totalPages}>Next</button>
            </nav>
          )}
        </section>
      </main>

      <footer className="site-footer">Amrita Publications index <span>|</span> source records link back to amrita.edu</footer>
      <DetailDialog record={selectedRecord} saved={selectedRecord ? savedIds.has(String(selectedRecord.id)) : false} onSave={toggleSaved} onClose={() => setSelectedRecord(null)} />
    </div>
  );
}
